import { Container, Singleton } from 'typescript-ioc/es5'
import { AppService } from './app'
import { axiosFetch } from '../config/axios-setup'
import { AppState } from '../stores/app'
import aqe from '@pushly/aqe'
import { IApiCallOptions } from '@pushly/aqe/lib/interfaces'
import { IServiceApiResponse } from '../interfaces/service-api-response'
import { handleResponseErrorMessage } from '../_utils/response-error-utils'
import querystring from 'query-string'
import { DomainSecret } from '@pushly/models/lib/structs/domain-secrets/domain-secret'
import { CreateDomainSecretRequestDto } from '@pushly/models/lib/structs/domain-secrets/create-domain-secret-request.dto'
import { UpdateDomainSecretRequestDto } from '@pushly/models/lib/structs/domain-secrets/update-domain-secret-request.dto'
import { DomainSecretType } from '@pushly/models/lib/enums/domain-secret-type'

@Singleton
export class DomainSecretsService {
    public appService: AppService
    public appState: AppState

    public constructor() {
        this.appService = Container.get(AppService)
        this.appState = Container.get(AppState)
    }

    public async fetchAll(
        domainId: number,
        options: IApiCallOptions = {},
    ): Promise<IServiceApiResponse<DomainSecret[]>> {
        const res: IServiceApiResponse<DomainSecret[]> = {
            ok: false,
            data: [],
        }

        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const qs = options.query ? `?${querystring.stringify(options.query)}` : ''
            const req = await axiosFetch('get', {
                url: `${this.getEndpoint(domainId)}${qs}`,
            })

            const { data: reqData, ...meta } = req.data

            res.data = reqData.map((secret) => DomainSecret.parseJsonObject(secret)) ?? []
            res.meta = meta
            res.ok = true
        } catch (error) {
            res.error = error
            handleResponseErrorMessage(error, {
                onCancelled: () => (res.cancelled = true),
            })
        }

        if (options.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return res
    }

    public async create(
        domainId: number,
        secret: CreateDomainSecretRequestDto,
        options: IApiCallOptions = {},
    ): Promise<IServiceApiResponse<DomainSecret | null>> {
        const res: IServiceApiResponse<DomainSecret | null> = {
            ok: false,
            data: null,
        }

        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const payload: Record<string, string | undefined> = {
                alias: secret.alias,
                type: secret.type as any,
            } as any

            if (secret.type === DomainSecretType.USERPASS) {
                payload.username = secret.username
                payload.password = secret.password
            } else {
                payload.value = secret.value
            }

            const req = await axiosFetch('post', {
                url: this.getEndpoint(domainId),
                data: payload,
            })

            const { data: reqData, ...meta } = req.data

            res.ok = true
            res.data = reqData ? DomainSecret.parseJsonObject(reqData) : null
            res.meta = meta
        } catch (error) {
            res.error = error
            handleResponseErrorMessage(error, {
                onCancelled: () => (res.cancelled = true),
            })
        }

        if (options.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return res
    }

    public async updateSecret(
        domainId: number,
        secretId: number,
        secret: UpdateDomainSecretRequestDto,
        options: IApiCallOptions = {},
    ): Promise<IServiceApiResponse<DomainSecret | null>> {
        const res: IServiceApiResponse<DomainSecret | null> = {
            ok: false,
            data: null,
        }

        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const payload: Record<string, string | undefined> = {}

            if (secret.username !== undefined || secret.password !== undefined) {
                payload.username = secret.username
                payload.password = secret.password
            } else {
                payload.value = secret.value
            }

            const req = await axiosFetch('patch', {
                url: `${this.getEndpoint(domainId)}/${secretId}`,
                data: payload,
            })

            const { data: reqData, ...meta } = req.data

            res.ok = true
            res.data = reqData ? DomainSecret.parseJsonObject(reqData) : null
            res.meta = meta
        } catch (error) {
            res.error = error
            handleResponseErrorMessage(error, {
                onCancelled: () => (res.cancelled = true),
            })
        }

        if (options.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return res
    }

    public async deleteSecret(domainId: number, secretId: number, options: IApiCallOptions = {}) {
        let ok = false

        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            await axiosFetch('delete', {
                url: `${this.getEndpoint(domainId)}/${secretId}`,
            })

            ok = true
        } catch (error) {
            handleResponseErrorMessage(error, {
                onCancelled: () => (ok = false),
            })
        }

        if (options.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok }
    }

    private getEndpoint = (id: number) => `${aqe.defaults.publicApiDomain}/v3/domains/${id}/secrets`
}
